import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { api } from '../App';
import { Plus, Edit2, Trash2, AlertCircle, Settings, Check, X, ChevronDown, ChevronUp, ArrowUpDown, Filter, Building2, User, Hash, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { formatINR, stripCommas } from '../lib/numberFormat';

const LASManagement = ({ user, setUser }) => {
  const [accounts, setAccounts] = useState([]);
  const [members, setMembers] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [expandedAccountId, setExpandedAccountId] = useState(null);
  const [pledgeViewMode, setPledgeViewMode] = useState('scrip'); // 'scrip' or 'member'
  
  // Filter and sort states
  const [sortConfig, setSortConfig] = useState({ key: 'bank_name', direction: 'asc' });
  const [filters, setFilters] = useState({
    bankName: '',
    memberName: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  const [formData, setFormData] = useState({
    member_id: '',
    bank_name: '',
    account_number: '',
    loan_amount: 0,
    interest_rate: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [accountsRes, membersRes, holdingsRes] = await Promise.all([
        api.get('/las-accounts'),
        api.get('/family-members'),
        api.get('/holdings'),
      ]);
      setAccounts(accountsRes.data);
      setMembers(membersRes.data);
      setHoldings(holdingsRes.data);
    } catch (err) {
      toast.error('Failed to load LAS accounts');
    } finally {
      setLoading(false);
    }
  };

  const getMemberName = (memberId) => {
    const member = members.find(m => m.id === memberId);
    return member ? member.name : 'Unknown';
  };

  // Get unique bank names for filter dropdown
  const uniqueBanks = useMemo(() => {
    return [...new Set(accounts.map(a => a.bank_name))].sort();
  }, [accounts]);

  // Get unique member names for filter dropdown
  const uniqueMembers = useMemo(() => {
    const memberIds = [...new Set(accounts.map(a => a.member_id))];
    return memberIds.map(id => ({
      id,
      name: getMemberName(id)
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [accounts, members]);

  // Filter and sort accounts
  const filteredAndSortedAccounts = useMemo(() => {
    let result = [...accounts];
    
    // Apply filters
    if (filters.bankName) {
      result = result.filter(a => a.bank_name === filters.bankName);
    }
    if (filters.memberName) {
      result = result.filter(a => a.member_id === filters.memberName);
    }
    if (filters.search) {
      const search = filters.search.toLowerCase();
      result = result.filter(a => 
        a.bank_name.toLowerCase().includes(search) ||
        a.account_number.toLowerCase().includes(search) ||
        getMemberName(a.member_id).toLowerCase().includes(search)
      );
    }
    
    // Helper to calculate pledged value for sorting
    const getPledgedValue = (accountId) => {
      const pledgedHoldings = holdings.filter((h) => h.las_account_id === accountId && h.pledged);
      return pledgedHoldings.reduce((sum, h) => sum + (h.purchase_price || 0) * (h.quantity || 0), 0);
    };
    
    // Apply sorting
    result.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'bank_name':
          aVal = a.bank_name || '';
          bVal = b.bank_name || '';
          break;
        case 'member':
          aVal = getMemberName(a.member_id);
          bVal = getMemberName(b.member_id);
          break;
        case 'account_number':
          aVal = a.account_number || '';
          bVal = b.account_number || '';
          break;
        case 'loan_amount':
          aVal = a.loan_amount || 0;
          bVal = b.loan_amount || 0;
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        case 'pledged_value':
          aVal = getPledgedValue(a.id);
          bVal = getPledgedValue(b.id);
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        default:
          aVal = a.bank_name || '';
          bVal = b.bank_name || '';
      }
      
      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return result;
  }, [accounts, filters, sortConfig, members, holdings]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const clearFilters = () => {
    setFilters({ bankName: '', memberName: '', search: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/las-accounts', formData);
      toast.success('LAS account created');
      setOpen(false);
      setFormData({
        member_id: '',
        bank_name: '',
        account_number: '',
        loan_amount: 0,
        interest_rate: 0,
      });
      fetchData();
    } catch (err) {
      toast.error('Failed to create account');
    }
  };

  const handleEdit = (account) => {
    setEditingId(account.id);
    setEditFormData({
      member_id: account.member_id,
      bank_name: account.bank_name,
      account_number: account.account_number,
      loan_amount: account.loan_amount,
      interest_rate: account.interest_rate,
    });
  };

  const handleSaveEdit = async (id) => {
    try {
      await api.put(`/las-accounts/${id}`, editFormData);
      toast.success('LAS account updated');
      setEditingId(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to update account');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFormData({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this LAS account?')) return;
    try {
      await api.delete(`/las-accounts/${id}`);
      toast.success('Account deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete account');
    }
  };

  const updateLoan = async (accountId, newAmount) => {
    try {
      await api.patch(`/las-accounts/${accountId}?loan_amount=${newAmount}`);
      toast.success('Loan amount updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update loan');
    }
  };

  const calculatePledgedValue = (accountId) => {
    const pledgedHoldings = holdings.filter((h) => h.las_account_id === accountId && h.pledged);
    return pledgedHoldings.reduce((sum, h) => sum + h.purchase_price * h.quantity, 0);
  };

  const getPledgedHoldings = (accountId) => {
    return holdings.filter((h) => h.las_account_id === accountId && h.pledged);
  };

  const getPledgedHoldingsGrouped = (accountId, groupBy) => {
    const pledgedHoldings = getPledgedHoldings(accountId);
    
    if (groupBy === 'scrip') {
      // Group by symbol
      const grouped = {};
      pledgedHoldings.forEach(h => {
        if (!grouped[h.symbol]) {
          grouped[h.symbol] = {
            symbol: h.symbol,
            totalQuantity: 0,
            totalValue: 0,
            holdings: []
          };
        }
        grouped[h.symbol].totalQuantity += h.quantity;
        grouped[h.symbol].totalValue += h.purchase_price * h.quantity;
        grouped[h.symbol].holdings.push(h);
      });
      return Object.values(grouped).sort((a, b) => b.totalValue - a.totalValue);
    } else {
      // Group by member
      const grouped = {};
      pledgedHoldings.forEach(h => {
        const memberName = getMemberName(h.member_id);
        if (!grouped[h.member_id]) {
          grouped[h.member_id] = {
            memberId: h.member_id,
            memberName,
            totalQuantity: 0,
            totalValue: 0,
            holdings: []
          };
        }
        grouped[h.member_id].totalQuantity += h.quantity;
        grouped[h.member_id].totalValue += h.purchase_price * h.quantity;
        grouped[h.member_id].holdings.push(h);
      });
      return Object.values(grouped).sort((a, b) => b.totalValue - a.totalValue);
    }
  };

  const calculateMonthlyInterest = (loanAmount, interestRate) => {
    return (loanAmount * interestRate) / 100 / 12;
  };

  const toggleExpand = (accountId) => {
    setExpandedAccountId(expandedAccountId === accountId ? null : accountId);
  };

  const renderSortButton = (sortKey, label) => (
    <button
      onClick={() => handleSort(sortKey)}
      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
        sortConfig.key === sortKey 
          ? 'bg-primary text-white' 
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {label}
      {sortConfig.key === sortKey ? (
        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
      ) : (
        <ArrowUpDown size={14} />
      )}
    </button>
  );

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="text-center py-20">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div data-testid="las-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">LAS Accounts</h1>
            <p className="text-gray-600">Manage Loan Against Shares accounts</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setEditMode(!editMode)}
              className={`btn-outline flex items-center gap-2 ${editMode ? 'bg-amber-50 border-amber-500 text-amber-700' : ''}`}
            >
              <Settings size={18} />
              {editMode ? 'Exit Edit Mode' : 'Edit Mode'}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-las-button" className="btn-primary flex items-center gap-2">
                  <Plus size={20} />
                  Add LAS Account
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create LAS Account</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Member *</label>
                    <select
                      data-testid="las-member-select"
                      className="input-field w-full"
                      value={formData.member_id}
                      onChange={(e) => setFormData({ ...formData, member_id: e.target.value })}
                      required
                    >
                      <option value="">Select Member</option>
                      {members.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name} ({m.relation})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Bank Name *</label>
                    <input
                      type="text"
                      data-testid="las-bank-input"
                      className="input-field w-full"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Account Number *</label>
                    <input
                      type="text"
                      data-testid="las-account-input"
                      className="input-field w-full"
                      value={formData.account_number}
                      onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Initial Loan Amount</label>
                    <input
                      type="text" inputMode="decimal"
                      data-testid="las-loan-input"
                     
                      className="input-field w-full"
                      value={formatINR(formData.loan_amount)}
                      onChange={(e) => setFormData({ ...formData, loan_amount: parseFloat(stripCommas(e.target.value)) })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Interest Rate (% per annum) *</label>
                    <input
                      type="text" inputMode="decimal"
                      data-testid="las-interest-input"
                     
                      className="input-field w-full"
                      value={formatINR(formData.interest_rate)}
                      onChange={(e) => setFormData({ ...formData, interest_rate: parseFloat(stripCommas(e.target.value)) })}
                      required
                    />
                  </div>
                  <Button type="submit" data-testid="submit-las-button" className="btn-primary w-full">
                    Create Account
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filter and Sort Controls */}
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setShowFilters(!showFilters)}
                className="btn-outline flex items-center gap-2"
              >
                <Filter size={16} />
                Filters {(filters.bankName || filters.memberName || filters.search) && '•'}
              </Button>
              {(filters.bankName || filters.memberName || filters.search) && (
                <button onClick={clearFilters} className="text-sm text-primary hover:underline">
                  Clear all
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Sort by:</span>
              {renderSortButton('bank_name', 'Bank')}
              {renderSortButton('member', 'Member')}
              {renderSortButton('pledged_value', 'Pledged Value')}
              {renderSortButton('loan_amount', 'Loan Amount')}
            </div>
          </div>
          
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
              <div>
                <label className="block text-sm font-medium mb-1">Bank Name</label>
                <select
                  className="input-field w-full"
                  value={filters.bankName}
                  onChange={(e) => setFilters({ ...filters, bankName: e.target.value })}
                >
                  <option value="">All Banks</option>
                  {uniqueBanks.map(bank => (
                    <option key={bank} value={bank}>{bank}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Member</label>
                <select
                  className="input-field w-full"
                  value={filters.memberName}
                  onChange={(e) => setFilters({ ...filters, memberName: e.target.value })}
                >
                  <option value="">All Members</option>
                  {uniqueMembers.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Search</label>
                <input
                  type="text"
                  className="input-field w-full"
                  placeholder="Search bank, account, member..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                />
              </div>
            </div>
          )}
          
          <div className="mt-3 text-sm text-gray-600">
            Showing {filteredAndSortedAccounts.length} of {accounts.length} accounts
          </div>
        </div>

        {editMode && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
            ⚠️ Edit Mode is ON - You can now edit or delete accounts. Click Exit Edit Mode when done.
          </div>
        )}

        {filteredAndSortedAccounts.length === 0 ? (
          <div className="card text-center py-12" data-testid="empty-las">
            <p className="text-gray-500">
              {accounts.length === 0 
                ? "No LAS accounts yet. Create your first account."
                : "No accounts match your filters."}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredAndSortedAccounts.map((account) => {
              const member = members.find((m) => m.id === account.member_id);
              const pledgedValue = calculatePledgedValue(account.id);
              const maxLoan = pledgedValue * 0.5;
              const availableCredit = maxLoan - account.loan_amount;
              const utilization = maxLoan > 0 ? (account.loan_amount / maxLoan) * 100 : 0;
              const monthlyInterest = calculateMonthlyInterest(account.loan_amount, account.interest_rate);
              const isExpanded = expandedAccountId === account.id;
              const pledgedHoldings = getPledgedHoldings(account.id);
              const groupedHoldings = getPledgedHoldingsGrouped(account.id, pledgeViewMode);

              return (
                <div key={account.id} className="card" data-testid={`las-account-${account.id}`}>
                  {editingId === account.id ? (
                    // Edit Form
                    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-semibold text-lg mb-3">Edit LAS Account</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Bank Name</label>
                          <input
                            type="text"
                            className="input-field w-full"
                            value={editFormData.bank_name}
                            onChange={(e) => setEditFormData({ ...editFormData, bank_name: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Account Number</label>
                          <input
                            type="text"
                            className="input-field w-full"
                            value={editFormData.account_number}
                            onChange={(e) => setEditFormData({ ...editFormData, account_number: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Loan Amount</label>
                          <input
                            type="text" inputMode="decimal"
                            className="input-field w-full"
                            value={formatINR(editFormData.loan_amount)}
                            onChange={(e) => setEditFormData({ ...editFormData, loan_amount: parseFloat(stripCommas(e.target.value)) })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Interest Rate (%)</label>
                          <input
                            type="text" inputMode="decimal"
                           
                            className="input-field w-full"
                            value={formatINR(editFormData.interest_rate)}
                            onChange={(e) => setEditFormData({ ...editFormData, interest_rate: parseFloat(stripCommas(e.target.value)) })}
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button onClick={() => handleSaveEdit(account.id)} className="btn-primary flex items-center gap-1">
                          <Check size={16} /> Save
                        </Button>
                        <Button onClick={handleCancelEdit} className="btn-outline flex items-center gap-1">
                          <X size={16} /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Account Header - Clickable to expand */}
                      <div 
                        className="flex items-start justify-between cursor-pointer"
                        onClick={() => toggleExpand(account.id)}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${isExpanded ? 'bg-primary text-white' : 'bg-gray-100'}`}>
                            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <Building2 size={18} className="text-gray-500" />
                              <h3 className="text-xl font-bold">{account.bank_name}</h3>
                            </div>
                            <p className="text-gray-600 flex items-center gap-2 mt-1">
                              <User size={14} /> {member?.name} 
                              <span className="text-gray-400">•</span>
                              <Hash size={14} /> {account.account_number}
                            </p>
                          </div>
                        </div>
                        {editMode && (
                          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleEdit(account)}
                              className="p-2 hover:bg-blue-50 rounded-lg text-blue-600"
                            >
                              <Edit2 size={18} />
                            </button>
                            <button
                              data-testid={`delete-las-${account.id}`}
                              onClick={() => handleDelete(account.id)}
                              className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Summary Stats - Always visible */}
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4 pt-4 border-t">
                        <div className="p-3 bg-green-50 rounded-lg">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Pledged Value</p>
                          <p className="text-lg font-bold text-green-700">₹{pledgedValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Max Loan (50%)</p>
                          <p className="text-lg font-bold text-blue-700">₹{maxLoan.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                        </div>
                        <div className="p-3 bg-orange-50 rounded-lg">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Current Loan</p>
                          <p className="text-lg font-bold text-orange-700">₹{account.loan_amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                        </div>
                        <div className={`p-3 rounded-lg ${availableCredit >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Available Credit</p>
                          <p className={`text-lg font-bold ${availableCredit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                            ₹{availableCredit.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                          </p>
                        </div>
                        <div className={`p-3 rounded-lg ${utilization > 90 ? 'bg-red-50' : 'bg-gray-50'}`}>
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Utilization</p>
                          <p className={`text-lg font-bold ${utilization > 90 ? 'text-red-700' : 'text-gray-700'}`}>
                            {utilization.toFixed(1)}%
                          </p>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t animate-in slide-in-from-top-2 duration-200">
                          {/* Interest Info */}
                          <div className="flex items-center gap-6 mb-4 p-3 bg-gray-50 rounded-lg">
                            <div>
                              <span className="text-sm text-gray-500">Interest Rate:</span>
                              <span className="ml-2 font-semibold">{account.interest_rate}% p.a.</span>
                            </div>
                            <div>
                              <span className="text-sm text-gray-500">Monthly Interest:</span>
                              <span className="ml-2 font-semibold">₹{monthlyInterest.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                            </div>
                            <div>
                              <span className="text-sm text-gray-500">Pledged Holdings:</span>
                              <span className="ml-2 font-semibold">{pledgedHoldings.length} items</span>
                            </div>
                          </div>

                          {/* Utilization Warning */}
                          {utilization > 90 && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3 mb-4">
                              <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
                              <div>
                                <p className="font-semibold text-sm text-red-800 mb-1">High Utilization Warning</p>
                                <p className="text-sm text-red-700">
                                  Your loan utilization is {utilization.toFixed(1)}%. Consider pledging more shares or reducing the loan amount.
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Pledged Holdings Section */}
                          {pledgedHoldings.length > 0 && (
                            <div className="mt-4">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold text-lg flex items-center gap-2">
                                  <Briefcase size={18} />
                                  Pledged Holdings
                                </h4>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-gray-500">View by:</span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setPledgeViewMode('scrip'); }}
                                    className={`px-3 py-1 text-sm rounded-lg ${pledgeViewMode === 'scrip' ? 'bg-primary text-white' : 'bg-gray-100'}`}
                                  >
                                    Scrip
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setPledgeViewMode('member'); }}
                                    className={`px-3 py-1 text-sm rounded-lg ${pledgeViewMode === 'member' ? 'bg-primary text-white' : 'bg-gray-100'}`}
                                  >
                                    Member
                                  </button>
                                </div>
                              </div>

                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b bg-gray-50">
                                      <th className="text-left py-2 px-3 font-semibold">
                                        {pledgeViewMode === 'scrip' ? 'Symbol' : 'Member'}
                                      </th>
                                      <th className="text-right py-2 px-3 font-semibold">Quantity</th>
                                      <th className="text-right py-2 px-3 font-semibold">Value</th>
                                      <th className="text-right py-2 px-3 font-semibold">% of Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {groupedHoldings.map((group, idx) => (
                                      <tr key={idx} className="border-b hover:bg-gray-50">
                                        <td className="py-2 px-3 font-medium">
                                          {pledgeViewMode === 'scrip' ? group.symbol : group.memberName}
                                        </td>
                                        <td className="py-2 px-3 text-right">{group.totalQuantity.toLocaleString()}</td>
                                        <td className="py-2 px-3 text-right font-semibold">
                                          ₹{group.totalValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                        </td>
                                        <td className="py-2 px-3 text-right text-gray-600">
                                          {pledgedValue > 0 ? ((group.totalValue / pledgedValue) * 100).toFixed(1) : 0}%
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot>
                                    <tr className="bg-gray-100 font-semibold">
                                      <td className="py-2 px-3">Total</td>
                                      <td className="py-2 px-3 text-right">
                                        {pledgedHoldings.reduce((sum, h) => sum + h.quantity, 0).toLocaleString()}
                                      </td>
                                      <td className="py-2 px-3 text-right">
                                        ₹{pledgedValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                      </td>
                                      <td className="py-2 px-3 text-right">100%</td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </div>
                          )}

                          {pledgedHoldings.length === 0 && (
                            <div className="text-center py-8 text-gray-500">
                              No shares pledged to this account yet.
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="mt-4 flex gap-3" onClick={(e) => e.stopPropagation()}>
                            <button
                              data-testid={`edit-loan-${account.id}`}
                              onClick={() => {
                                const newAmount = prompt('Enter new loan amount:', account.loan_amount);
                                if (newAmount && !isNaN(newAmount)) {
                                  updateLoan(account.id, parseFloat(newAmount));
                                }
                              }}
                              className="btn-outline flex items-center gap-2"
                            >
                              <Edit2 size={16} />
                              Update Loan
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default LASManagement;
